import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import closePopupOperation from 'editor/src/store/app/module/popup/operation/closePopupOperation';
import getPopupData from 'editor/src/store/app/module/popup/selector/getPopupData';
import { ParsedFontInfo } from 'editor/src/store/fonts/utils/parseFontFile';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import Button from 'editor/src/component/Button';
import AbstractPopup, { PopupType } from 'editor/src/component/Popup/AbstractPopup';
import Section from 'editor/src/component/Popup/AbstractPopup/Section';
import { PopupName } from 'editor/src/component/Popup/popups';

import styles from './index.module.scss';

// SVG path data for status icons (extracted to avoid max-len warnings)
const CHECK_ICON_PATH =
  'M8.589 13.004a.77.77 0 0 1-.226-.044.65.65 0 0 1-.212-.146L5.687 10.35a.476.476 0 0 1' +
  '-.167-.445c.005-.18.065-.328.182-.445.117-.116.263-.175.438-.175s.321.058.438.175l2.042' +
  ' 2.042L13.446 6.675a.45.45 0 0 1 .43-.175c.17 0 .313.058.43.175a.49.49 0 0 1 .175.43' +
  '.49.49 0 0 1-.175.43L9.026 12.815a.65.65 0 0 1-.212.146.77.77 0 0 1-.226.044Z';

const WARNING_ICON_PATH =
  'M4.648 14.471c-.234 0-.411-.102-.533-.307-.122-.204-.124-.408-.008-.612L9.475' +
  ' 4.306c.117-.194.292-.306.525-.306s.408.102.525.306l5.367 9.246c.117.204.114' +
  '.408-.008.612-.121.204-.299.307-.533.307H4.648ZM10.015 8.068a.58.58 0 0 0-.43' +
  '.183.58.58 0 0 0-.183.43v1.633c0 .166.06.309.183.431a.58.58 0 0 0 .43.183.58' +
  '.58 0 0 0 .43-.183.58.58 0 0 0 .183-.43V8.68a.58.58 0 0 0-.183-.431.58.58 0' +
  ' 0 0-.43-.183Zm0 4.652a.58.58 0 0 0 .43-.182.58.58 0 0 0 .183-.431.58.58 0 0' +
  ' 0-.183-.43.58.58 0 0 0-.43-.183.58.58 0 0 0-.43.182.58.58 0 0 0-.183.431c0' +
  ' .165.06.309.183.43a.58.58 0 0 0 .43.183Z';

// Store File objects outside Redux to avoid non-serializable state warnings
export const pendingUploadFiles = new Map<string, File>();

export interface FontUploadPopupData {
  parsedFonts: Array<ParsedFontInfo & { filename: string; isDuplicate?: boolean }>;
}

function FontUploadPopup() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const popupData = useSelector((state) => getPopupData(state, PopupName.FONT_UPLOAD_POPUP));
  const [isLicenseChecked, setIsLicenseChecked] = useState(false);
  const [isProtected, setIsProtected] = useState(false);

  const handleClose = useCallback(() => {
    setIsLicenseChecked(false);
    setIsProtected(false);
    dispatch(closePopupOperation());
  }, [dispatch]);

  const handleUpload = useCallback(async () => {
    if (!popupData) {
      return;
    }

    // Only upload valid, non-duplicate fonts
    const fontsToUpload = popupData.parsedFonts.filter((f) => f.isValid && !f.isDuplicate);

    // Convert File objects to base64 so they can cross the postMessage boundary
    const files = await Promise.all(
      fontsToUpload.map(async (f) => {
        const file = pendingUploadFiles.get(f.fontFile);
        let fileData = '';
        if (file) {
          const buffer = await file.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i += 1) {
            binary += String.fromCharCode(bytes[i]);
          }
          fileData = btoa(binary);
        }
        return {
          name: f.name,
          family: f.family,
          fontFile: f.fontFile,
          filename: f.filename,
          fileData,
          weight: f.weight,
          style: f.style,
        };
      }),
    );

    sendPostMessage('fonts.uploadRequested', { files, isProtected });
    pendingUploadFiles.clear();
    setIsLicenseChecked(false);
    setIsProtected(false);
    dispatch(closePopupOperation());
  }, [popupData, dispatch, isProtected]);

  if (!popupData) {
    return null;
  }

  const { parsedFonts } = popupData;
  const newFonts = parsedFonts.filter((f) => f.isValid && !f.isDuplicate);
  const duplicateFonts = parsedFonts.filter((f) => f.isDuplicate);
  const isSingleFont = newFonts.length === 1;
  const hasOnlyDuplicates = newFonts.length === 0 && duplicateFonts.length > 0;

  return (
    <AbstractPopup
      onClickOutside={handleClose}
      name={PopupName.FONT_UPLOAD_POPUP}
      type={PopupType.INFO}
      title={t('txt_editor_font_upload_title')}
      hideTitleIcon
      buttons={
        <div className={styles.buttons}>
          <Button secondary onClick={handleClose}>
            {t('txt_editor_font_upload_cancel')}
          </Button>
          <Button primary onClick={handleUpload} disabled={!isLicenseChecked || newFonts.length === 0}>
            {t('txt_editor_font_upload_action')}
          </Button>
        </div>
      }
    >
      <Section>
        {!hasOnlyDuplicates && (
          <p
            className={styles.description}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: isSingleFont
                ? t('txt_editor_font_upload_description_single')
                : t('txt_editor_font_upload_description'),
            }}
          />
        )}

        {newFonts.length > 0 && (
          <div className={styles.fontList}>
            {newFonts.map((font) => (
              <div key={font.fontFile} className={styles.fontItem}>
                <span className={styles.fontIcon}>T</span>
                <div className={styles.fontInfo}>
                  <span className={styles.fontName}>{font.name}</span>
                  <span className={styles.fontFilename}>{font.filename}</span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={styles.statusIcon}
                >
                  <rect width="20" height="20" rx="10" fill="#32A06E" />
                  <path d={CHECK_ICON_PATH} fill="white" />
                </svg>
              </div>
            ))}
          </div>
        )}

        {duplicateFonts.length > 0 && (
          <>
            <p className={styles.duplicateLabel}>{t('txt_editor_font_upload_duplicates')}</p>
            <div className={styles.fontList}>
              {duplicateFonts.map((font) => (
                <div key={font.fontFile} className={styles.fontItem}>
                  <span className={styles.fontIcon}>T</span>
                  <div className={styles.fontInfo}>
                    <span className={styles.fontName}>{font.name}</span>
                    <span className={styles.fontFilename}>{font.filename}</span>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className={styles.statusIcon}
                  >
                    <rect width="20" height="20" rx="10" fill="#E5A500" />
                    <path d={WARNING_ICON_PATH} fill="white" />
                  </svg>
                </div>
              ))}
            </div>
          </>
        )}

        {!hasOnlyDuplicates && (
          <div className={styles.checkboxGroup}>
            <label className={styles.checkbox}>
              <input type="checkbox" checked={isProtected} onChange={(e) => setIsProtected(e.target.checked)} />
              <span>{t('txt_editor_font_upload_protected_checkbox')}</span>
            </label>

            {isProtected && (
              <div className={styles.protectedWarning}>
                <span className={styles.warningIcon}>⚠</span>
                <span>{t('txt_editor_font_upload_protected_warning')}</span>
              </div>
            )}

            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={isLicenseChecked}
                onChange={(e) => setIsLicenseChecked(e.target.checked)}
              />
              <span>
                {isSingleFont ? t('txt_editor_font_upload_checkbox_single') : t('txt_editor_font_upload_checkbox')}
                <span className={styles.requiredAsterisk}> *</span>
              </span>
            </label>
          </div>
        )}
      </Section>
    </AbstractPopup>
  );
}

export default FontUploadPopup;
